'use client'

import { useForm } from 'react-hook-form'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useTurnstile } from '@/Turnstile'
import { Button } from '@/General/Button'
import { useSubmitContactFormMutation } from '@/generated/graphql'
import { useRouter } from 'next/navigation'
import { useState, useEffect } from 'react'
import { useStore } from '@/store'
import { EMAIL_REGEX } from '@/validation'

type MagicCollectionFormData = {
  name: string
  email: string
  additionalInfo?: string
}

export const CollectionForm = () => {
  const user = useStore(state => state.user)
  const router = useRouter()
  const [formData, setFormData] = useState<MagicCollectionFormData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<MagicCollectionFormData>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      email: '',
      additionalInfo: '',
    },
  })

  useEffect(() => {
    if (user) {
      const userName = user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`.trim()
        : user.firstName || user.lastName || ''

      reset({
        name: userName,
        email: user.email,
        additionalInfo: '',
      })
    }
  }, [user, reset])

  const { executeTurnstile } = useTurnstile()

  const { mutate: submitContactForm } = useSubmitContactFormMutation({
    onSuccess: (result) => {
      setIsSubmitting(false)
      if (result.submitContactForm && formData) {
        const params = new URLSearchParams({
          email: formData.email,
        })
        router.push(`/magic-collections/thank-you?${params.toString()}`)
      } else {
        setError('root', {
          type: 'manual',
          message: 'Something went wrong with the submission. Please try again later or email us at magic@vanishingincmagic.com.',
        })
      }
    },
    onError: () => {
      setIsSubmitting(false)
      setError('root', {
        type: 'manual',
        message: 'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    },
  })

  const onSubmit = async (data: MagicCollectionFormData) => {
    if (isSubmitting) return

    setIsSubmitting(true)
    try {
      const token = await executeTurnstile()

      const response = await fetch('/api/verifycaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        setIsSubmitting(false)
        setError('root', {
          type: 'manual',
          message:
            'Unfortunately we were unable to verify that a human was submitting this form. Please try emailing us at magic@vanishingincmagic.com instead.',
        })
        return
      }

      setFormData(data)
      submitContactForm({
        name: data.name,
        email: data.email,
        reason: 'somethingelse',
        question: `Magic collection inquiry: ${data.additionalInfo || 'User is interested in selling their magic collection.'}`,
        orderNumber: null
      })
    } catch {
      setIsSubmitting(false)
      setError('root', {
        type: 'manual',
        message: 'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="needs-validation"
      noValidate
    >
      <ErrorBox
        errors={
          Object.values(errors)
            .map(error => error?.message)
            .filter(Boolean) as string[]
        }
      />

      <div className="mb-4">
        <input
          {...register('name', {
            required: 'Please enter your name',
          })}
          type="text"
          id="name"
          placeholder="Your name"
          className={`block w-full px-3 py-2 border ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          } rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg text-black`}
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">
            {errors.name.message}
          </p>
        )}
      </div>

      <div className="mb-4">
        <input
          {...register('email', {
            required: 'Please enter your email address',
            pattern: {
              value: EMAIL_REGEX,
              message: 'Invalid email address',
            },
          })}
          type="email"
          id="email"
          placeholder="Your email address"
          className={`block w-full px-3 py-2 border ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          } rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg text-black`}
        />
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="mb-4">
        <textarea
          {...register('additionalInfo', {
            required: 'Please tell us about your magic collection',
          })}
          id="additionalInfo"
          rows={4}
          placeholder="Tell us about your magic collection"
          className={`block w-full px-3 py-2 border ${
            errors.additionalInfo ? 'border-red-500' : 'border-gray-300'
          } rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg text-black`}
        />
        {errors.additionalInfo && (
          <p className="text-red-500 text-sm mt-1">
            {errors.additionalInfo.message}
          </p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
      </Button>
          </form>
  )
}
